import { Model, Factory, Response, belongsTo, hasMany } from 'miragejs'
import { pageable, sortable } from './utils.js'
import { randWord, randQuote, randBetweenDate, randNumber } from '@ngneat/falso'

import type { MirageServer, MockServer } from './server'
import type { ModelDefinition } from 'miragejs/-types'
import { addToDate } from '@/utils/date/calculation.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { safeMathRandom } from '@/utils/number.js'

const today = getTodayDateTimeISO()
const yesterday = addToDate(getTodayDateTimeISO(), { days: -1 })

// Commenting out to get past TS errors and replace with type below
// export type BillingNoteData = SchemaAttributes<'Note'>

export type BillingNoteData = {
  title: string
  note: string
  created: string
  modified: string
  note_type: number
  archived: string | null
  locked: boolean
}

export const model = (Model as ModelDefinition<BillingNoteData>).extend({
  patients: hasMany('patient'),
  createdUser: belongsTo('user'),
  createdDepartment: belongsTo('department'),
  client: belongsTo('client'),
  attachments: hasMany('upload'),
})

export const factory = Factory.extend({
  title() {
    return (
      randWord({ length: 1, capitalize: true })[0] +
      randWord({ length: 3 }).join(' ')
    )
  },
  note() {
    return randQuote({ maxCharCount: 200 })
  },
  created() {
    return randBetweenDate({
      to: today,
      from: yesterday,
    }).toISOString()
  },
  modified() {
    return randBetweenDate({
      to: today,
      from: yesterday,
    }).toISOString()
  },
  note_type() {
    return 14
  },
  archived() {
    return randNumber({ min: 0, max: 1 }) < 0.25 // 25% chance of notes being archived
      ? randBetweenDate({
          from: yesterday,
          to: today,
        }).toISOString()
      : null
  },
  locked() {
    return randNumber({ min: 0, max: 1 }) < 0.25 // 25% chance of notes being locked
  },
})

export function routes(server: MockServer) {
  server.get('/notes', function (this: MirageServer, schema, request) {
    const billingNotes = schema.all('billingNote')

    const sorted = sortable(billingNotes, request)
    const { data, meta } = pageable(sorted, request)

    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get('/notes/:id', function (this: MirageServer, schema, request) {
    const id = request.params.id
    const billingNote = schema.find('billingNote', id)

    if (!billingNote) {
      return new Response(404, {}, { errors: [{ detail: 'Note not found' }] })
    }

    return billingNote
  })

  server.post('/notes', function (db, request) {
    const requestBody = JSON.parse(request.requestBody)

    const dataToSave = {
      ...requestBody.data.attributes,
      created: getTodayDateTimeISO(),
      created_user: null,
      archived: null,
      locked: false,
    }

    const newNote = db.create('billingNote', dataToSave)

    return newNote
  })

  server.patch('/notes/:id', function (db, request) {
    const id = request.params.id
    const billingNote = db.find('billingNote', id)

    if (!billingNote) {
      return new Response(404, {}, { errors: [{ detail: 'Note not found' }] })
    }

    billingNote.update({ archived: getTodayDateTimeISO() })
    return billingNote
  })

  server.put('/notes/:id', function (db, request) {
    const requestBody = JSON.parse(request.requestBody)

    const id = request.params.id
    const billingNote = db.find('billingNote', id)

    if (!billingNote) {
      return new Response(404, {}, { errors: [{ detail: 'Note not found' }] })
    }

    billingNote.update({
      ...requestBody.data.attributes,
    })

    return billingNote
  })

  // Handle POST requests to /notes/:id/upload for attachment uploads
  server.post('/notes/:id/upload', function (db, request) {
    const noteId = request.params.id
    const billingNote = db.find('billingNote', noteId)

    if (!billingNote) {
      return new Response(404, {}, { errors: [{ detail: 'Note not found' }] })
    }

    // Mock response matching the API documentation
    const uploadId = Math.floor(safeMathRandom() * 1000000)
    const fileName = 'uploaded_file.pdf'
    const fileSize = 17183
    const fileExtension = fileName.split('.').pop() || 'pdf'
    const randomPath = safeMathRandom().toString(36).substring(7)

    const mockResponse = {
      id: uploadId,
      file: `https://storage-reviewapps.provetcloud.com/d1d861/provet/4/attachments/9/c/8/${randomPath}.${fileExtension}`,
      original_name: fileName,
      size: fileSize,
      created: getTodayDateTimeISO(),
      group: {
        type: 'UploadGroup',
        id: safeMathRandom().toString(36).substring(7),
      },
      uploader: {
        type: 'User',
        id: '411',
      },
      expires: null,
    }

    return mockResponse
  })

  // Handle GET requests to /notes/:id/upload/:uploadId for fetching individual attachments
  server.get('/notes/:id/upload/:uploadId', function (db, request) {
    const noteId = request.params.id
    const uploadId = request.params.uploadId
    const billingNote = db.find('billingNote', noteId)

    if (!billingNote) {
      return new Response(404, {}, { errors: [{ detail: 'Note not found' }] })
    }

    // Mock response matching the API documentation
    const mockResponse = {
      id: parseInt(uploadId),
      file: `https://storage-reviewapps.provetcloud.com/d1d861/provet/4/attachments/0/8/b/${safeMathRandom().toString(36).substring(7)}.pdf`,
      original_name: 'test_file_1.pdf',
      size: 17183,
      created: getTodayDateTimeISO(),
      group: {
        type: 'UploadGroup',
        id: safeMathRandom().toString(36).substring(7),
      },
      uploader: {
        type: 'User',
        id: '411',
      },
      expires: null,
    }

    return mockResponse
  })

  // Handle DELETE requests to /notes/:id/upload/:uploadId for deleting attachments
  server.delete('/notes/:id/upload/:uploadId', function (db, request) {
    const noteId = request.params.id
    const billingNote = db.find('billingNote', noteId)

    if (!billingNote) {
      return new Response(404, {}, { errors: [{ detail: 'Note not found' }] })
    }

    // Return success response (204 No Content)
    return new Response(204, {}, {})
  })
}

export function seeds(server: MockServer) {
  const numberOfItems = 10
  const billingNotes = server.createList('billingNote', numberOfItems)

  // Create some attachments for billing notes
  billingNotes.forEach((billingNote) => {
    // Create 0-3 attachments per note randomly
    const attachmentCount = Math.floor(safeMathRandom() * 4)
    if (attachmentCount > 0) {
      const attachments = server.createList('upload', attachmentCount)
      // Associate attachments with the billing note
      billingNote.update({ attachmentIds: attachments.map((a) => a.id) })
    }
  })
}
